
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import ModalFullScreenNew from "@/components/modal/ModalFullScreenNew.vue";
import SmartMatchProfileCard from "./SmartMatchProfileCard.vue";
import { SmartMatchService, SmartMatchResult } from "@/services/smartMatchService";
import GetSmartMatchResults from "@/apollo/smartMatch/GetSmartMatchResults.gql";
import GetSmartMatchResultsByManager from "@/apollo/smartMatch/GetSmartMatchResultsByManager.gql";
import GetSmartMatchResultsByAdmin from "@/apollo/smartMatch/GetSmartMatchResultsByAdmin.gql";
import GetProjectBookmarks from "@/apollo/smartMatch/GetProjectBookmarks.gql";
import GetProjectBookmarksByManager from "@/apollo/smartMatch/GetProjectBookmarksByManager.gql";
import GetProjectBookmarksByAdmin from "@/apollo/smartMatch/GetProjectBookmarksByAdmin.gql";

interface FilterOption {
  key: string;
  label: string;
  active: boolean;
}

@Component({
  components: {
    ModalFullScreenNew,
    SmartMatchProfileCard,
  },
})
export default class SmartMatchResultsModal extends Vue {
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: String, required: true }) readonly projectId!: string;
  @Prop({ type: String, required: false, default: null }) readonly managerId!: string | null;
  @Prop({ type: String, required: false, default: null }) readonly adminId!: string | null;
  @Prop({ type: Boolean, required: false, default: false }) readonly isNewClient!: boolean;

  searchQuery: string = '';
  isLoading: boolean = false;
  profileResults: SmartMatchResult[] = [];
  smartMatchConfig: any = null;
  project: any = null;
  existingBookmarks: Map<string, any> = new Map();

  // Get current user ID from global prototype
  get currentUserId(): string {
    return (this as any).$currentUser?.id || '';
  }

  // Get filtered must-have filters based on client type
  get availableMustHaveFilters(): FilterOption[] {
    if (this.isNewClient) {
      // For new clients, exclude availability and location_radius
      return this.mustHaveFilters.filter(filter => 
        filter.key !== 'availability' && filter.key !== 'location_radius'
      );
    }
    return this.mustHaveFilters;
  }

  // Get filtered nice-to-have filters based on client type
  get availableNiceToHaveFilters(): FilterOption[] {
    if (this.isNewClient) {
      // For new clients, exclude previous_engagements and transportation
      return this.niceToHaveFilters.filter(filter => 
        filter.key !== 'previous_engagements' && filter.key !== 'transportation'
      );
    }
    return this.niceToHaveFilters;
  }

  mustHaveFilters: FilterOption[] = [
    { key: 'availability', label: 'Availability', active: false },
    { key: 'required_skills', label: 'Required Skills', active: false },
    { key: 'exclude_do_not_use', label: 'Exclude Do Not Use', active: false },
    { key: 'location_radius', label: 'Distance Filter', active: false },
    { key: 'preferred_language', label: 'Preferred Language', active: false },
  ];

  niceToHaveFilters: FilterOption[] = [
    { key: 'soft_skills', label: 'Soft Skills', active: false },
    { key: 'title_type_match', label: 'Title Match', active: false },
    { key: 'previous_engagements', label: 'Previous Engagements', active: false },
    { key: 'transportation', label: 'Transportation', active: false },
    { key: 'rating', label: 'Rating', active: false },
    { key: 'favourited_resource', label: 'Favourited Resource', active: false },
    { key: 'hours_available', label: 'Hours Available', active: false },
  ];

  // Component lifecycle
  async mounted() {
    // Validate we have either managerId or adminId
    if (!this.managerId && !this.adminId) {
      console.error('SmartMatchResultsModal: No managerId or adminId provided');
      return;
    }
    
    await this.loadExistingBookmarks();
    await this.loadSmartMatchResults();
  }

  // Watch for search query changes
  @Watch('searchQuery')
  async onSearchQueryChanged() {
    // Debounce search
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.loadSmartMatchResults();
    }, 500);
  }

  private searchTimeout: any = null;

  /**
   * Initialize filter toggles from Smart Match configuration
   */
  initializeFiltersFromConfig() {
    if (!this.smartMatchConfig) return;

    // Initialize must-have filters from config
    this.mustHaveFilters.forEach(filter => {
      switch (filter.key) {
        case 'availability':
          filter.active = this.smartMatchConfig.availability_enabled;
          break;
        case 'required_skills':
          filter.active = this.smartMatchConfig.required_skills_enabled;
          break;
        case 'exclude_do_not_use':
          filter.active = this.smartMatchConfig.exclude_do_not_use_enabled;
          break;
        case 'location_radius':
          filter.active = this.smartMatchConfig.location_radius_enabled;
          break;
        case 'preferred_language':
          filter.active = this.smartMatchConfig.preferred_language_enabled;
          break;
      }
    });

    // Initialize nice-to-have filters from config
    this.niceToHaveFilters.forEach(filter => {
      switch (filter.key) {
        case 'soft_skills':
          filter.active = this.smartMatchConfig.soft_skills_enabled;
          break;
        case 'title_type_match':
          filter.active = this.smartMatchConfig.title_type_match_enabled;
          break;
        case 'previous_engagements':
          filter.active = this.smartMatchConfig.previous_engagements_enabled;
          break;
        case 'transportation':
          filter.active = this.smartMatchConfig.transportation_enabled;
          break;
        case 'rating':
          filter.active = this.smartMatchConfig.rating_enabled;
          break;
        case 'favourited_resource':
          filter.active = this.smartMatchConfig.favourited_resource_enabled;
          break;
        case 'hours_available':
          filter.active = this.smartMatchConfig.hours_available_enabled;
          break;
      }
    });

  }

  /**
   * Create a temporary config with current filter overrides
   */
  createTemporaryConfig() {
    if (!this.smartMatchConfig) return this.smartMatchConfig;

    // Clone the original config
    const tempConfig = { ...this.smartMatchConfig };

    // Apply must-have filter overrides
    this.mustHaveFilters.forEach(filter => {
      switch (filter.key) {
        case 'availability':
          tempConfig.availability_enabled = filter.active;
          break;
        case 'required_skills':
          tempConfig.required_skills_enabled = filter.active;
          break;
        case 'exclude_do_not_use':
          tempConfig.exclude_do_not_use_enabled = filter.active;
          break;
        case 'location_radius':
          tempConfig.location_radius_enabled = filter.active;
          break;
        case 'preferred_language':
          tempConfig.preferred_language_enabled = filter.active;
          break;
      }
    });

    // Apply nice-to-have filter overrides
    this.niceToHaveFilters.forEach(filter => {
      switch (filter.key) {
        case 'soft_skills':
          tempConfig.soft_skills_enabled = filter.active;
          break;
        case 'title_type_match':
          tempConfig.title_type_match_enabled = filter.active;
          break;
        case 'previous_engagements':
          tempConfig.previous_engagements_enabled = filter.active;
          break;
        case 'transportation':
          tempConfig.transportation_enabled = filter.active;
          break;
        case 'rating':
          tempConfig.rating_enabled = filter.active;
          break;
        case 'favourited_resource':
          tempConfig.favourited_resource_enabled = filter.active;
          break;
        case 'hours_available':
          tempConfig.hours_available_enabled = filter.active;
          break;
      }
    });

    return tempConfig;
  }

  toggleMustHave(key: string) {
    const filter = this.mustHaveFilters.find(f => f.key === key);
    if (filter) {
      filter.active = !filter.active;
      console.log(`🔘 ${filter.label} toggled ${filter.active ? 'active' : 'inactive'}`);
    }
  }

  toggleNiceToHave(key: string) {
    const filter = this.niceToHaveFilters.find(f => f.key === key);
    if (filter) {
      filter.active = !filter.active;
      console.log(`🔘 ${filter.label} toggled ${filter.active ? 'active' : 'inactive'}`);
    }
  }

  /**
   * Fuzzy search contractors by name using Levenshtein distance
   */
  fuzzySearchContractors(contractors: any[], searchQuery: string): any[] {
    const query = searchQuery.toLowerCase();
    
    // Calculate similarity scores for all contractors
    const contractorsWithScores = contractors.map(contractor => {
      const fullName = `${contractor.user?.given_name || ''} ${contractor.user?.family_name || ''}`.trim().toLowerCase();
      const firstName = (contractor.user?.given_name || '').toLowerCase();
      const lastName = (contractor.user?.family_name || '').toLowerCase();
      
      // Calculate scores for different matching strategies
      const exactScore = this.calculateExactMatchScore(query, fullName, firstName, lastName);
      const fuzzyScore = this.calculateFuzzyScore(query, fullName);
      const partialScore = this.calculatePartialMatchScore(query, fullName, firstName, lastName);
      
      // Use the best score
      const bestScore = Math.max(exactScore, fuzzyScore, partialScore);
      
      return {
        contractor,
        score: bestScore,
        fullName
      };
    });

    // Filter contractors with reasonable similarity scores (threshold: 0.3)
    const filteredContractors = contractorsWithScores
      .filter(item => item.score >= 0.3)
      .sort((a, b) => b.score - a.score) // Sort by similarity score descending
      .map(item => item.contractor);

    return filteredContractors;
  }

  /**
   * Calculate exact match score
   */
  calculateExactMatchScore(query: string, fullName: string, firstName: string, lastName: string): number {
    if (fullName === query) return 1.0;
    if (firstName === query || lastName === query) return 0.9;
    if (fullName.startsWith(query) || query.startsWith(fullName)) return 0.8;
    return 0;
  }

  /**
   * Calculate fuzzy match score using Levenshtein distance
   */
  calculateFuzzyScore(query: string, fullName: string): number {
    const distance = this.levenshteinDistance(query, fullName);
    const maxLength = Math.max(query.length, fullName.length);
    return maxLength === 0 ? 1 : (maxLength - distance) / maxLength;
  }

  /**
   * Calculate partial match score
   */
  calculatePartialMatchScore(query: string, fullName: string, firstName: string, lastName: string): number {
    const words = query.split(' ').filter(word => word.length > 0);
    
    if (words.length === 0) return 0;
    
    let totalScore = 0;
    for (const word of words) {
      let wordScore = 0;
      
      // Check if word appears in full name
      if (fullName.includes(word)) {
        wordScore = Math.max(wordScore, 0.7);
      }
      
      // Check if word is similar to first or last name
      const firstNameSimilarity = this.calculateFuzzyScore(word, firstName);
      const lastNameSimilarity = this.calculateFuzzyScore(word, lastName);
      wordScore = Math.max(wordScore, firstNameSimilarity * 0.8, lastNameSimilarity * 0.8);
      
      totalScore += wordScore;
    }
    
    return totalScore / words.length;
  }

  /**
   * Calculate Levenshtein distance between two strings
   */
  levenshteinDistance(str1: string, str2: string): number {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
    
    for (let i = 0; i <= str1.length; i += 1) {
      matrix[0][i] = i;
    }
    
    for (let j = 0; j <= str2.length; j += 1) {
      matrix[j][0] = j;
    }
    
    for (let j = 1; j <= str2.length; j += 1) {
      for (let i = 1; i <= str1.length; i += 1) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1, // deletion
          matrix[j - 1][i] + 1, // insertion
          matrix[j - 1][i - 1] + indicator // substitution
        );
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  async refreshResults() {
    console.log('Refresh Results clicked');
    await this.loadExistingBookmarks();
    await this.loadSmartMatchResults();
  }

  /**
   * Load existing bookmarks for this project and manager/admin
   */
  async loadExistingBookmarks() {
    try {
      let data;
      
      if (this.adminId) {
        const result = await this.$apollo.query({
          query: GetProjectBookmarksByAdmin,
          variables: {
            admin_id: this.adminId,
            project_id: this.projectId
          },
          fetchPolicy: 'network-only'
        });
        data = result.data;
      } else if (this.managerId) {
        const result = await this.$apollo.query({
          query: GetProjectBookmarksByManager,
          variables: {
            manager_id: this.managerId,
            project_id: this.projectId
          },
          fetchPolicy: 'network-only'
        });
        data = result.data;
      } else {
        console.error('No managerId or adminId available for loading bookmarks');
        return;
      }

      // Store bookmarks in a Map for quick lookup by user_id
      this.existingBookmarks = new Map();
      if (data.smart_match_bookmark) {
        data.smart_match_bookmark.forEach((bookmark: any) => {
          this.existingBookmarks.set(bookmark.user_id, bookmark);
        });
      }
    } catch (error) {
      console.error('Error loading existing bookmarks:', error);
    }
  }

  /**
   * Load Smart Match results from GraphQL API
   */
  async loadSmartMatchResults() {
    this.isLoading = true;
    
    try {
      let data;
      
      if (this.adminId) {
        const result = await this.$apollo.query({
          query: GetSmartMatchResultsByAdmin,
          variables: {
            projectId: this.projectId,
            adminId: this.adminId,
            currentUserId: this.currentUserId,
            limit: 50
          },
        });
        data = result.data;
      } else if (this.managerId) {
        const result = await this.$apollo.query({
          query: GetSmartMatchResultsByManager,
          variables: {
            projectId: this.projectId,
            managerId: this.managerId,
            currentUserId: this.currentUserId,
            limit: 50
          },
        });
        data = result.data;
      } else {
        console.error('No managerId or adminId available for query');
        return;
      }
      // Extract configuration and project data
      this.smartMatchConfig = data.smart_match_configs?.[0];
      this.project = data.project_by_pk;

      if (!this.smartMatchConfig) {
        console.log('No Smart Match configuration found for manager/admin');
        this.profileResults = [];
        return;
      }

      // Initialize filter toggles from the loaded config (only on first load)
      if (this.mustHaveFilters.every(f => !f.active) && this.niceToHaveFilters.every(f => !f.active)) {
        this.initializeFiltersFromConfig();
      }

      if (!this.smartMatchConfig.smart_match_enabled) {
        console.log('Smart Match is disabled for this manager/admin');
        this.profileResults = [];
        return;
      }

      if (!this.project) {
        console.log('Client not found');
        this.profileResults = [];
        return;
      }

      // Add safety checks for required fields
      if (!this.project.firstname && !this.project.lastname) {
        console.log('Client missing client_name information');
      }

      // Process contractors through Smart Match algorithm using temporary config
      const contractors = data.contractor || [];
      const activeConfig = this.createTemporaryConfig();

      // Apply search filter if search query exists
      let filteredContractors = contractors;
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        filteredContractors = this.fuzzySearchContractors(contractors, this.searchQuery.trim());
      }

      this.profileResults = await SmartMatchService.processSmartMatchResults(
        filteredContractors,
        this.project,
        activeConfig,
        this.isNewClient
      );

      // Sync bookmark state with existing bookmarks
      this.syncBookmarkState();

      
    } catch (error) {
      console.log('Error loading Smart Match results:', error);
      this.profileResults = [];
      
      // Show user-friendly error message
      if (error.message?.includes("field '")) {
        console.log('Database schema mismatch. Please check field names in GraphQL query.');
      } else if (error.message?.includes('GraphQL error')) {
        console.log('GraphQL query error:', error.message);
      } else {
        console.log('Unexpected error during Smart Match search');
      }
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Sync bookmark state from database with profile results
   */
  syncBookmarkState() {
    this.profileResults.forEach(profile => {
      const bookmark = this.existingBookmarks.get(profile.userId);
      profile.bookmarked = !!bookmark;
    });
  }

  handleFavoriteToggled(profileId: string, favorited: boolean) {
    const profile = this.profileResults.find(p => p.id === profileId);
    if (profile) {
      profile.favorited = favorited;
      // If removing favorite, clear the favoriteId
      if (!favorited) {
        profile.favoriteId = undefined;
      }
    }
    console.log(`💖 Favorite toggled for ${profile?.name}: ${favorited ? 'Added' : 'Removed'}`);
  }

  handleBookmarkToggled(profileId: string, bookmarked: boolean) {
    const profile = this.profileResults.find(p => p.id === profileId);
    if (profile) {
      profile.bookmarked = bookmarked;
      
      // Update the existing bookmarks map
      if (bookmarked) {
        // Add to bookmarks map (we might not have the full bookmark data, but we know it exists)
        this.existingBookmarks.set(profile.userId, {
          user_id: profile.userId,
          match_percentage: profile.matchPercentage,
          bookmarked_at: new Date().toISOString()
        });
      } else {
        // Remove from bookmarks map
        this.existingBookmarks.delete(profile.userId);
      }
    }
    console.log(`Bookmark toggled for ${profileId}: ${bookmarked}`);
  }

  get bookmarkedCount(): number {
    return this.profileResults.filter(profile => profile.bookmarked).length;
  }

  handleChatClicked(profileId: string) {
    console.log(`Chat clicked for ${profileId}`);
  }

  closeModal() {
    this.$emit('close');
  }
}
